/* eslint-disable i18next/no-literal-string */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Container } from 'src/components/Container';
import { Dialog } from 'src/components/Dialog';
import { Grid } from 'src/components/Grid';
import { PageHeader } from 'src/components/PageHeader';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { isTrainer } from 'src/models/user/utils';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

interface FoodItem {
  id: number;
  name: string;
  brand: string | null;
  category: string | null;
  serving_sizes: Array<{
    unit: string;
    grams: number;
    amount: number;
  }>;
  source: string;
  external_provider: string | null;
  external_id: string | null;
  calories_per_gram: string;
  protein_per_gram: string;
  carbohydrates_per_gram: string;
  fat_per_gram: string;
  fiber_per_gram: string;
  is_favorite: boolean;
}

interface FoodLogEntry {
  id: number;
  quantity: string;
  unit: string;
  calories: string;
  protein: string;
  carbohydrates: string;
  fat: string;
  fiber: string;
  is_favorite: boolean;
  notes: string | null;
  created_at: string;
  food_item: FoodItem;
}

interface FoodLog {
  id: number;
  meal_type: string;
  name: string;
  is_searchable: boolean;
  notes: string | null;
  created_at: string;
  food_log_entries: FoodLogEntry[];
}

interface DailyLog {
  id: number;
  log_date: string;
  total_calories: string;
  total_protein: string;
  total_carbohydrates: string;
  total_fat: string;
  total_fiber: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  food_logs: FoodLog[];
}

// Helper to get today's date in YYYY-MM-DD format (local timezone)
const getTodayDateString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fetchDailyLog = async (): Promise<DailyLog> => {
  const { data } = await api.get('/api/v4/nutrition/daily_log', {
    params: { date: getTodayDateString() },
  });
  return data;
};

interface Summary {
  total_calories: number;
  calories_goal: number;
  total_protein: number;
  total_carbohydrates: number;
  total_fat: number;
  total_fiber: number;
  macros: {
    protein: {
      current: number;
      goal?: number;
      remaining?: number;
    };
    carbs: {
      current: number;
      goal?: number;
      remaining?: number;
    };
    fat: {
      current: number;
      goal?: number;
      remaining?: number;
    };
    fiber: {
      current: number;
      goal?: number;
      remaining?: number;
    };
  };
}

interface TimelineDay {
  date: string;
  log_date: string;
  intake?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
  target?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
  weight?: number;
  goal?: {
    id: number;
    name: string;
    input_mode: string;
  };
}

const fetchSummary = async (
  viewRemaining: boolean,
  startDate: string,
  endDate: string
): Promise<Summary> => {
  const { data } = await api.get('/api/v4/nutrition/summary', {
    params: {
      start_date: startDate,
      end_date: endDate,
      view_remaining: viewRemaining,
    },
  });
  return data;
};

interface GroupedSummary {
  [key: string]: {
    calories: { current: number };
    carbs: { current: number };
    protein: { current: number };
    fat: { current: number };
    fiber: { current: number };
  };
}

const fetchGroupedSummary = async (
  groupBy: 'by_day_week' | 'by_date',
  startDate: string,
  endDate: string
): Promise<GroupedSummary> => {
  const { data } = await api.get('/api/v4/nutrition/summary', {
    params: {
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    },
  });
  return data;
};

interface CreateCustomFoodItemPayload {
  food_item: {
    name: string;
    brand?: string;
    category?: string;
    serving_sizes: Array<{
      unit: string;
      amount: number;
      grams: number;
    }>;
    calories_per_gram: number;
    protein_per_gram: number;
    carbohydrates_per_gram: number;
    fat_per_gram: number;
    fiber_per_gram: number;
  };
}

interface CreateFoodLogEntriesPayload {
  entries: Array<{
    food_item_id: number;
    food_log_type: string;
    serving_size: number;
    serving_unit: string;
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  }>;
}

const createCustomFoodItem = async (): Promise<{
  food_item: { id: number };
}> => {
  const payload: CreateCustomFoodItemPayload = {
    food_item: {
      name: `Dummy Food ${Date.now()}`,
      brand: 'Test Brand',
      category: 'test',
      serving_sizes: [
        { unit: 'serving', amount: 1, grams: 100 },
        { unit: 'cup', amount: 1, grams: 200 },
      ],
      calories_per_gram: 2.0,
      protein_per_gram: 0.2,
      carbohydrates_per_gram: 0.3,
      fat_per_gram: 0.1,
      fiber_per_gram: 0.05,
    },
  };
  const { data } = await api.post('/api/v4/nutrition/food_items', payload);
  return data;
};

const createFoodLogEntry = async (
  foodItemId: number,
  mealType: string,
  macros: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  },
  servingSize: number = 1,
  servingUnit: string = 'serving'
): Promise<any> => {
  const payload: CreateFoodLogEntriesPayload = {
    entries: [
      {
        food_item_id: foodItemId,
        food_log_type: mealType,
        serving_size: servingSize,
        serving_unit: servingUnit,
        calories: macros.calories,
        protein: macros.protein,
        carbs: macros.carbs,
        fat: macros.fat,
        fiber: macros.fiber,
      },
    ],
  };
  const { data } = await api.post(
    '/api/v4/nutrition/food_log_entries',
    payload,
    {
      params: { date: getTodayDateString() },
    }
  );
  return data;
};

interface NutritionSummary {
  serving_description: string;
  calories: string;
  protein: string;
  carbs: string;
  fat: string;
  fiber?: string;
}

interface SearchResult {
  id: number | null;
  name: string;
  brand?: string;
  category?: string;
  description?: string;
  source: string;
  external_id?: string;
  external_provider?: string;
  is_favorite: boolean;
  food_url?: string;
  nutrition_summary?: NutritionSummary;
  servings?: Array<{
    serving_id?: string;
    serving_description?: string;
    serving_url?: string;
    metric_serving_amount?: string;
    metric_serving_unit?: string;
    number_of_units?: string;
    measurement_description?: string;
    unit?: string;
    amount?: number;
    grams?: number;
    calories?: string;
    carbohydrate?: string;
    protein?: string;
    fat?: string;
    saturated_fat?: string;
    polyunsaturated_fat?: string;
    monounsaturated_fat?: string;
    trans_fat?: string;
    cholesterol?: string;
    sodium?: string;
    potassium?: string;
    fiber?: string;
    sugar?: string;
    vitamin_a?: string;
    vitamin_c?: string;
    calcium?: string;
    iron?: string;
  }>;
}

interface GoalMeta {
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  fiber?: number;
}

interface Goal {
  id: number;
  name: string;
  input_mode: string;
  meta: GoalMeta;
}

interface CurrentGoalResponse {
  goal: Goal | null;
  nutrition_goal_settings: NutritionGoalSettings;
  date: string;
  user_id: number;
  message?: string;
}

interface FitnessStat {
  value: number;
  stat: string;
  created_at: number;
  stat_type: string | null;
  user_id: number;
  logger_id: number | null;
  phase: string | null;
  copied_at: number | null;
  metric: string | null;
  nutrition_tracking_note: string | null;
}

interface NutritionGoalSettings {
  enable_fiber_goal?: boolean;
  enable_rest_day_goals?: boolean;
  track_net_carbs?: boolean;
  customize_calories_per_gram?: boolean;
  enable_custom_day_goal_schedule?: boolean;
  track_net_carbs_array?: string[];
  customized_calories?: {
    protein?: number;
    carbs?: number;
    fat?: number;
  };
}

interface ScheduleEntry {
  day_of_week: number;
  goal_id: number;
  name?: string;
  input_mode?: string;
  meta?: GoalMeta;
}

interface NutritionNote {
  id: number;
  title: string;
  notes: string;
  date: number; // Unix timestamp
  image?: {
    url: string;
    thumb_url: string;
    medium_url: string;
    filename: string;
    content_type: string;
    byte_size: number;
  };
  created_by_id: number;
  user_id: number;
  notable_type: string;
}

const searchFoodItems = async (
  query: string,
  favoritesOnly: boolean = false,
  customOnly: boolean = false
): Promise<SearchResult[]> => {
  const { data } = await api.get('/api/v4/nutrition/food_items', {
    params: {
      search_expression: query || undefined,
      max_results: 20,
      favorites_only: favoritesOnly,
      custom_only: customOnly,
    },
  });

  // Extract necessary fields from food_items
  return data.food_items.map((item: any) => ({
    id: item.id,
    name: item.name,
    brand: item.brand,
    category: item.category,
    description: item.description,
    source: item.source,
    external_id: item.external_id,
    external_provider: item.external_provider,
    is_favorite: item.is_favorite || false,
    nutrition_summary: item.nutrition_summary,
    servings: item.servings || [],
    food_url: item.food_url,
  }));
};

const fetchFoodItemDetails = async (
  externalId: string,
  externalProvider: string
): Promise<SearchResult> => {
  const { data } = await api.get('/api/v4/nutrition/food_items/details', {
    params: {
      external_id: externalId,
      external_provider: externalProvider,
    },
  });
  return data.food_item;
};

const addToFavorites = async (
  foodItemId?: number | null,
  externalId?: string | null,
  externalProvider?: string | null
): Promise<void> => {
  const payload: any = {};

  if (foodItemId) {
    payload.food_item_id = foodItemId;
  } else if (externalId && externalProvider) {
    payload.external_id = externalId;
    payload.external_provider = externalProvider;
  } else {
    throw new Error(
      'Must provide either food_item_id or external_id + external_provider'
    );
  }

  await api.post('/api/v4/nutrition/favorite_foods', payload);
};

const removeFromFavorites = async (favoriteId: number): Promise<void> => {
  await api.delete(`/api/v4/nutrition/favorite_foods/${favoriteId}`);
};

const fetchFavorites = async (): Promise<any[]> => {
  const { data } = await api.get('/api/v4/nutrition/favorite_foods');
  return data.favorite_foods || [];
};

const fetchGoals = async (): Promise<Goal[]> => {
  const { data } = await api.get('/api/v4/nutrition/goals');
  return data;
};

const fetchCurrentGoal = async (): Promise<CurrentGoalResponse> => {
  const { data } = await api.get('/api/v4/nutrition/goals/current');
  return data;
};

const createGoal = async (goalData: {
  name: string;
  input_mode: string;
  meta: any;
}): Promise<Goal> => {
  const { data } = await api.post('/api/v4/nutrition/goals', {
    goal: goalData,
  });
  return data;
};

const updateGoal = async (
  id: number,
  goalData: Partial<{
    name: string;
    input_mode: string;
    meta: any;
  }>
): Promise<Goal> => {
  const { data } = await api.put(`/api/v4/nutrition/goals/${id}`, {
    goal: goalData,
  });
  return data;
};

const deleteGoal = async (id: number): Promise<void> => {
  await api.delete(`/api/v4/nutrition/goals/${id}`);
};

interface UserNutritionSettings {
  rest_days: number[];
  nutrition_goals_schedule: ScheduleEntry[];
  nutrition_goal_settings: NutritionGoalSettings;
}

const fetchUserNutritionSettings = async (
  userId?: number
): Promise<UserNutritionSettings> => {
  const params = userId ? { user_id: userId } : {};
  const { data } = await api.get('/api/v4/nutrition/user_settings', { params });
  return data;
};

const updateUserNutritionSettings = async (
  settings: Partial<UserNutritionSettings>,
  userId?: number
): Promise<UserNutritionSettings> => {
  const params = userId ? { user_id: userId } : {};
  const { data } = await api.put('/api/v4/nutrition/user_settings', { user_setting: settings }, {
    params,
  });
  return data;
};

// Day of week helpers
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fetchNutritionNotes = async (
  userId: number,
  date?: string
): Promise<NutritionNote[]> => {
  const params: any = {
    user_id: userId,
    'q[notable_type_eq]': 'Nutrition',
  };

  if (date) {
    // Send full datetime range for the entire day
    params['q[date_gteq]'] = `${date} 00:00:00`;
    params['q[date_lteq]'] = `${date} 23:59:59`;
  }

  const { data } = await api.get('/api/v4/user_notes', { params });
  return Array.isArray(data) ? data : data.user_note || [];
};

const createNutritionNote = async (payload: {
  title: string;
  notes: string;
  date: Date;
  user_id: number;
  image?: File;
}): Promise<NutritionNote> => {
  const formData = new FormData();

  const timestamp = Math.floor(payload.date.getTime() / 1000);

  formData.append('user_note[title]', payload.title);
  formData.append('user_note[notes]', payload.notes);
  formData.append('user_note[date]', timestamp.toString());
  formData.append('user_note[user_id]', payload.user_id.toString());
  formData.append('user_note[notable_type]', 'Nutrition');

  if (payload.image) {
    formData.append('user_note[image]', payload.image);
  }

  const { data } = await api.post('/api/v4/user_notes', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

const updateNutritionNote = async (
  id: number,
  payload: {
    title?: string;
    notes?: string;
    date?: Date;
    image?: File;
    removeImage?: boolean;
  }
): Promise<NutritionNote> => {
  const formData = new FormData();

  if (payload.title !== undefined)
    formData.append('user_note[title]', payload.title);
  if (payload.notes !== undefined)
    formData.append('user_note[notes]', payload.notes);
  if (payload.date) {
    const timestamp = Math.floor(payload.date.getTime() / 1000);
    formData.append('user_note[date]', timestamp.toString());
  }
  if (payload.removeImage) {
    formData.append('user_note[remove_image]', 'true');
  }
  if (payload.image) {
    formData.append('user_note[image]', payload.image);
  }

  const { data } = await api.put(`/api/v4/user_notes/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

const deleteNutritionNote = async (id: number): Promise<void> => {
  await api.delete(`/api/v4/user_notes/${id}`);
};

// Fitness Stats API functions
const fetchWeightStats = async (): Promise<FitnessStat[]> => {
  const now = Math.floor(Date.now() / 1000);
  const threeMonthsAgo = now - 90 * 24 * 60 * 60; // 90 days ago
  const { data } = await api.get('/api/v3/fitness_stats', {
    params: {
      start: threeMonthsAgo,
      end: now,
      stat_ids: 'weight',
    },
  });
  return data;
};

interface Client {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

const fetchClients = async (): Promise<Client[]> => {
  const { data } = await api.get('/api/v4/users', {
    params: {
      'q[roles_mask_not_eq]': 2, // Exclude trainers
      page: 1,
      per: 1000,
    },
  });

  // Handle different response formats
  if (Array.isArray(data)) {
    return data;
  }
  if (data?.users && Array.isArray(data.users)) {
    return data.users;
  }
  if (data?.user && Array.isArray(data.user)) {
    return data.user;
  }
  return [];
};

const createWeightStat = async (
  weight: number,
  note?: string
): Promise<FitnessStat[]> => {
  const now = Math.floor(Date.now() / 1000);
  const payload = {
    start: now,
    stats: [
      {
        stat: 'weight',
        value: weight,
        label: 'Weight',
        ...(note ? { nutrition_tracking_note: note } : {}),
      },
    ],
  };
  const { data } = await api.post('/api/v3/fitness_stats', payload);
  return data;
};

const getTimelineDateRange = (range: string) => {
  const today = new Date();
  const endDate = new Date(today);
  let startDate = new Date(today);

  switch (range) {
    case 'this_week':
      startDate.setDate(today.getDate() - 6);
      break;
    case 'this_month':
      startDate.setMonth(today.getMonth() - 1);
      break;
    case 'last_3_months':
      startDate.setMonth(today.getMonth() - 3);
      break;
    case 'this_year':
      startDate = new Date(today.getFullYear(), 0, 1);
      break;
  }

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start: formatDate(startDate),
    end: formatDate(endDate),
  };
};

const fetchTimelineData = async (
  startDate: string,
  endDate: string,
  comparison: 'intake' | 'target' | null
): Promise<TimelineDay[]> => {
  const params: any = {
    start_date: startDate,
    end_date: endDate,
    response_format: 'timeline',
  };

  if (comparison) {
    params.comparison = comparison;
  }

  const { data } = await api.get('/api/v4/nutrition/summary', { params });
  return data.data || [];
};

// Helper to get date ranges
const getDateRange = (range: string): { start: string; end: string } => {
  const today = new Date();
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  switch (range) {
    case 'today': {
      const todayStr = formatDate(today);
      return { start: todayStr, end: todayStr };
    }
    case 'this_week': {
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ...
      const monday = new Date(today);
      monday.setDate(today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)); // Get Monday
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: formatDate(monday), end: formatDate(sunday) };
    }
    case 'this_month': {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start: formatDate(firstDay), end: formatDate(lastDay) };
    }
    case 'last_3_months': {
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      return { start: formatDate(threeMonthsAgo), end: formatDate(today) };
    }
    case 'this_year': {
      const firstDay = new Date(today.getFullYear(), 0, 1);
      const lastDay = new Date(today.getFullYear(), 11, 31);
      return { start: formatDate(firstDay), end: formatDate(lastDay) };
    }
    default:
      return { start: formatDate(today), end: formatDate(today) };
  }
};

const NutritionTestPage = () => {
  const queryClient = useQueryClient();
  const { user } = useSession();
  const [mealType, setMealType] = useState('breakfast');
  const [searchQuery, setSearchQuery] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  // Quick Log state
  const [quickLogOpen, setQuickLogOpen] = useState(false);
  const [quickLogMacros, setQuickLogMacros] = useState({
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    fiber: '',
  });
  const [customOnly, setCustomOnly] = useState(false);
  const [selectedFoodItem, setSelectedFoodItem] = useState<SearchResult | null>(
    null
  );
  const [foodItemModalOpen, setFoodItemModalOpen] = useState(false);
  const [foodItemDetails, setFoodItemDetails] = useState<SearchResult | null>(
    null
  );
  const [selectedServing, setSelectedServing] = useState<number>(0); // Index of selected serving
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [editQuantity, setEditQuantity] = useState<string>('');
  const [editUnit, setEditUnit] = useState<string>('');
  const [editingGoalId, setEditingGoalId] = useState<number | null>(null);
  const [editGoalName, setEditGoalName] = useState<string>('');
  const [editGoalMetadata, setEditGoalMetadata] = useState<any>({});
  const [editingGoalSettings, setEditingGoalSettings] = useState(false);
  const [editGoalSettingsData, setEditGoalSettingsData] =
    useState<Partial<NutritionGoalSettings>>({});
  const [viewRemaining, setViewRemaining] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteImage, setNoteImage] = useState<File | null>(null);
  const [noteImagePreview, setNoteImagePreview] = useState<string | null>(null);
  const [shouldRemoveImage, setShouldRemoveImage] = useState(false);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [summaryDateRange, setSummaryDateRange] = useState('today');
  const [weeklyDateRange, setWeeklyDateRange] = useState('this_week');
  const [dailyDateRange, setDailyDateRange] = useState('this_week');
  const [timelineDateRange, setTimelineDateRange] = useState('this_week');
  const [timelineComparison, setTimelineComparison] = useState<
    'intake' | 'target' | null
  >(null);
  const [newWeight, setNewWeight] = useState<string>('');
  const [newWeightNote, setNewWeightNote] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  // Rest days state (for editing user's rest days)
  const [editingRestDays, setEditingRestDays] = useState<number[] | null>(null);

  const [editingGoalsSchedule, setEditingGoalsSchedule] =
    useState<ScheduleEntry[] | null>(null);

  const {
    data: dailyLog,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dailyLog'],
    queryFn: fetchDailyLog,
  });

  const summaryDates = getDateRange(summaryDateRange);

  const {
    data: summary,
    isLoading: isLoadingSummary,
    error: summaryError,
  } = useQuery({
    queryKey: ['summary', viewRemaining, summaryDateRange],
    queryFn: () =>
      fetchSummary(viewRemaining, summaryDates.start, summaryDates.end),
  });

  const weeklyDates = getDateRange(weeklyDateRange);

  const { data: weeklySummary, isLoading: isLoadingWeeklySummary } = useQuery({
    queryKey: ['weeklySummary', weeklyDateRange],
    queryFn: () =>
      fetchGroupedSummary('by_day_week', weeklyDates.start, weeklyDates.end),
  });

  const dailyDates = getDateRange(dailyDateRange);

  const { data: dailySummary, isLoading: isLoadingDailySummary } = useQuery({
    queryKey: ['dailySummary', dailyDateRange],
    queryFn: () =>
      fetchGroupedSummary('by_date', dailyDates.start, dailyDates.end),
  });

  const { data: nutritionNotes, isLoading: isLoadingNotes } = useQuery({
    queryKey: ['nutritionNotes', user?.id, getTodayDateString()],
    queryFn: () =>
      fetchNutritionNotes(Number(user?.id) || 0, getTodayDateString()),
    enabled: !!user?.id,
  });

  // Check if selected food item already exists in the CURRENT meal type (not the entire daily log)
  const existingEntry =
    selectedFoodItem && dailyLog && dailyLog?.food_logs && mealType
      ? dailyLog.food_logs
          .filter((log) => log.meal_type === mealType) // ← Filter by current meal_type only
          .flatMap((log) => log.food_log_entries)
          .find((entry) => {
            // For custom items, compare by id
            if (selectedFoodItem.id) {
              return entry.food_item.id === selectedFoodItem.id;
            }
            // For external items, compare by external_id
            if (selectedFoodItem.external_id) {
              return (
                entry.food_item.external_id === selectedFoodItem.external_id
              );
            }
            return false;
          })
      : null;

  const {
    data: searchResults,
    isLoading: isSearching,
    refetch: performSearch,
  } = useQuery({
    queryKey: ['foodSearch', searchQuery, favoritesOnly, customOnly],
    queryFn: () => searchFoodItems(searchQuery, favoritesOnly, customOnly),
    enabled: false, // Only run when explicitly called
  });

  // Query to fetch full details of external food items
  const { refetch: fetchDetails, isFetching: isLoadingDetails } = useQuery({
    queryKey: [
      'foodItemDetails',
      selectedFoodItem?.external_id,
      selectedFoodItem?.external_provider,
    ],
    queryFn: () =>
      fetchFoodItemDetails(
        selectedFoodItem!.external_id!,
        selectedFoodItem!.external_provider!
      ),
    enabled: false, // Only run when explicitly called
  });

  const { data: goals, isLoading: isLoadingGoals } = useQuery({
    queryKey: ['goals'],
    queryFn: fetchGoals,
  });

  const { data: currentGoal, isLoading: isLoadingCurrentGoal } = useQuery({
    queryKey: ['currentGoal'],
    queryFn: fetchCurrentGoal,
  });

  const { data: weightStats, isLoading: isLoadingWeightStats } = useQuery({
    queryKey: ['weightStats'],
    queryFn: fetchWeightStats,
  });

  const timelineRange = getTimelineDateRange(timelineDateRange);
  const { data: timelineData, isLoading: isLoadingTimeline } = useQuery({
    queryKey: [
      'timelineData',
      timelineRange.start,
      timelineRange.end,
      timelineComparison,
    ],
    queryFn: () =>
      fetchTimelineData(
        timelineRange.start,
        timelineRange.end,
        timelineComparison
      ),
  });

  const { data: clients, isLoading: isLoadingClients } = useQuery({
    queryKey: ['clients'],
    queryFn: fetchClients,
    enabled: isTrainer(user), // Only fetch clients if user is a trainer
  });

  // Fetch user's nutrition settings (rest_days, nutrition_goals_schedule)
  const { data: userNutritionSettings, isLoading: isLoadingUserSettings } =
    useQuery({
      queryKey: ['userNutritionSettings'],
      queryFn: () => fetchUserNutritionSettings(),
    });

  const addCustomEntryMutation = useMutation({
    mutationFn: async () => {
      const foodItem = await createCustomFoodItem();
      // For custom items, use dummy macros (backend will calculate from per_gram values)
      const dummyMacros = {
        calories: 200,
        protein: 20,
        carbs: 30,
        fat: 10,
        fiber: 5,
      };
      await createFoodLogEntry(foodItem.food_item.id, mealType, dummyMacros);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyLog'] });
      queryClient.invalidateQueries({ queryKey: ['summary'] });
      queryClient.invalidateQueries({ queryKey: ['timelineData'] });
      toast.success(`Custom food entry added to ${mealType}!`);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: async (entryId: number) => {
      await api.delete(`/api/v4/nutrition/food_log_entries/${entryId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyLog'] });
      queryClient.invalidateQueries({ queryKey: ['summary'] });
      queryClient.invalidateQueries({ queryKey: ['timelineData'] });
      toast.success('Food entry deleted!');
      setSelectedFoodItem(null);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const addToFavoritesMutation = useMutation({
    mutationFn: ({
      foodItemId,
      externalId,
      externalProvider,
    }: {
      foodItemId?: number | null;
      externalId?: string | null;
      externalProvider?: string | null;
    }) => addToFavorites(foodItemId, externalId, externalProvider),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['foodSearch'] });

      // Optimistically update the cache for immediate visual feedback
      const queryKey = ['foodSearch', searchQuery, favoritesOnly, customOnly];
      queryClient.setQueryData(queryKey, (old: SearchResult[] | undefined) => {
        if (!old) return old;

        return old.map((item) => {
          // Check if this is the item we're adding to favorites
          const matches =
            (variables.foodItemId && item.id === variables.foodItemId) ||
            (variables.externalId &&
              item.external_id === variables.externalId &&
              item.external_provider === variables.externalProvider);

          return matches ? { ...item, is_favorite: true } : item;
        });
      });
    },
    onSuccess: () => {
      // Re-fetch in background to sync with backend
      performSearch();
      toast.success('Added to favorites!');
    },
    onError: (error: any) => {
      // Re-fetch to get correct data from backend
      performSearch();
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const removeFromFavoritesMutation = useMutation({
    mutationFn: ({ favoriteId }: { favoriteId: number; item: SearchResult }) =>
      removeFromFavorites(favoriteId),
    onMutate: async ({ item }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['foodSearch'] });

      // Optimistically update the cache for immediate visual feedback
      const queryKey = ['foodSearch', searchQuery, favoritesOnly, customOnly];
      queryClient.setQueryData(queryKey, (old: SearchResult[] | undefined) => {
        if (!old) return old;

        return old.map((searchItem) => {
          // Check if this is the item we're removing from favorites
          const matches =
            (item.id && searchItem.id === item.id) ||
            (item.external_id &&
              searchItem.external_id === item.external_id &&
              searchItem.external_provider === item.external_provider);

          return matches ? { ...searchItem, is_favorite: false } : searchItem;
        });
      });
    },
    onSuccess: () => {
      // Re-fetch in background to sync with backend
      performSearch();
      toast.success('Removed from favorites!');
    },
    onError: (error: any) => {
      // Re-fetch to get correct data from backend
      performSearch();
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const createGoalMutation = useMutation({
    mutationFn: createGoal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['summary'] });
      toast.success('Goal created!');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const updateGoalMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      updateGoal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['summary'] });
      queryClient.invalidateQueries({ queryKey: ['timelineData'] });
      toast.success('Goal updated!');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const deleteGoalMutation = useMutation({
    mutationFn: deleteGoal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['summary'] });
      queryClient.invalidateQueries({ queryKey: ['timelineData'] });
      toast.success('Goal deleted!');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const updateUserNutritionSettingsMutation = useMutation({
    mutationFn: (settings: Partial<UserNutritionSettings>) =>
      updateUserNutritionSettings(settings),
    onSuccess: () => {
      toast.success('Settings saved successfully!');
      setEditingRestDays(null);
      setEditingGoalsSchedule(null);
      setEditingGoalSettings(false);
      setEditGoalSettingsData({});
      queryClient.invalidateQueries({ queryKey: ['userNutritionSettings'] });
      queryClient.invalidateQueries({ queryKey: ['currentGoal'] });
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const createNoteMutation = useMutation({
    mutationFn: createNutritionNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nutritionNotes'] });
      toast.success('Note added successfully!');
      setIsAddingNote(false);
      setNoteTitle('');
      setNoteText('');
      setNoteImage(null);
      setNoteImagePreview(null);
      setShouldRemoveImage(false);
      setExistingImageUrl(null);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      updateNutritionNote(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nutritionNotes'] });
      toast.success('Note updated!');
      setEditingNoteId(null);
      setNoteTitle('');
      setNoteText('');
      setNoteImage(null);
      setNoteImagePreview(null);
      setShouldRemoveImage(false);
      setExistingImageUrl(null);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: deleteNutritionNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nutritionNotes'] });
      toast.success('Note deleted!');
    },
  });

  const createWeightMutation = useMutation({
    mutationFn: ({ weight, note }: { weight: number; note?: string }) =>
      createWeightStat(weight, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weightStats'] });
      toast.success('Weight logged!');
      setNewWeight('');
      setNewWeightNote('');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const updateEntryMutation = useMutation({
    mutationFn: async ({
      entryId,
      quantity,
      unit,
    }: {
      entryId: number;
      quantity: number;
      unit: string;
    }) => {
      // Get the entry to calculate new macros
      const entry = dailyLog?.food_logs
        .flatMap((log) => log.food_log_entries)
        .find((e) => e.id === entryId);

      if (!entry) throw new Error('Entry not found');

      // Calculate new macros based on quantity change
      const quantityRatio = quantity / parseFloat(entry.quantity);
      const newCalories = parseFloat(entry.calories) * quantityRatio;
      const newProtein = parseFloat(entry.protein) * quantityRatio;
      const newCarbs = parseFloat(entry.carbohydrates) * quantityRatio;
      const newFat = parseFloat(entry.fat) * quantityRatio;
      const newFiber = parseFloat(entry.fiber) * quantityRatio;

      await api.put(`/api/v4/nutrition/food_log_entries/${entryId}`, {
        serving_size: quantity,
        serving_unit: unit,
        calories: newCalories,
        protein: newProtein,
        carbs: newCarbs,
        fat: newFat,
        fiber: newFiber,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyLog'] });
      queryClient.invalidateQueries({ queryKey: ['summary'] });
      queryClient.invalidateQueries({ queryKey: ['timelineData'] });
      toast.success('Entry updated!');
      setEditingEntryId(null);
      setEditQuantity('');
      setEditUnit('');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  // Quick Log mutation
  const createQuickLogMutation = useMutation({
    mutationFn: async () => {
      const { calories, protein, carbs, fat, fiber } = quickLogMacros;

      // Validate that at least calories is provided
      if (!calories || parseFloat(calories) <= 0) {
        throw new Error('Calories is required and must be greater than 0');
      }

      await api.post('/api/v4/nutrition/food_log_entries/quick_log', {
        meal_type: mealType,
        date: getTodayDateString(),
        calories: parseFloat(calories),
        protein: parseFloat(protein || '0'),
        carbs: parseFloat(carbs || '0'),
        fat: parseFloat(fat || '0'),
        fiber: parseFloat(fiber || '0'),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyLog'] });
      queryClient.invalidateQueries({ queryKey: ['summary'] });
      queryClient.invalidateQueries({ queryKey: ['timelineData'] });
      toast.success(`Quick log added to ${mealType}!`);
      setQuickLogOpen(false);
      setQuickLogMacros({
        calories: '',
        protein: '',
        carbs: '',
        fat: '',
        fiber: '',
      });
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const addSelectedFoodEntryMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFoodItem) {
        throw new Error('No food item selected');
      }

      if (!foodItemDetails) {
        throw new Error('No food item details loaded');
      }

      let foodItemId: number;
      let macros = {
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        fiber: 0,
      };
      let servingUnit = 'serving';
      let servingSize = 1;

      // Get selected serving details
      const selectedServingData =
        foodItemDetails.servings && foodItemDetails.servings[selectedServing];

      // If it's a custom item (has id), use it directly
      if (foodItemDetails.id) {
        foodItemId = foodItemDetails.id;

        // For custom items, extract macros from the selected serving
        if (selectedServingData) {
          macros = {
            calories: parseFloat(selectedServingData.calories || '0') || 0,
            protein: parseFloat(selectedServingData.protein || '0') || 0,
            carbs: parseFloat(selectedServingData.carbohydrate || '0') || 0,
            fat: parseFloat(selectedServingData.fat || '0') || 0,
            fiber: parseFloat(selectedServingData.fiber || '0') || 0,
          };
          servingUnit = selectedServingData.unit || 'serving';
          servingSize = selectedServingData.amount || 1;
        }
      } else if (
        foodItemDetails.external_id &&
        foodItemDetails.external_provider
      ) {
        // For external items, create it first with full serving data
        const payload = {
          food_item: {
            source: 'fatsecret',
            external_id: foodItemDetails.external_id,
            external_provider: foodItemDetails.external_provider,
            name: foodItemDetails.name,
            brand: foodItemDetails.brand || null,
            category: foodItemDetails.category || null,
            serving_sizes: (foodItemDetails.servings || []).map((serving) => ({
              unit: serving.measurement_description || 'serving',
              amount: parseFloat(serving.number_of_units || '1'),
              grams: parseFloat(serving.metric_serving_amount || '100'),
            })),
          },
        };

        const { data } = await api.post(
          '/api/v4/nutrition/food_items',
          payload
        );
        foodItemId = data.food_item.id;

        // Extract macros from the SELECTED serving
        if (selectedServingData) {
          macros = {
            calories: parseFloat(selectedServingData.calories || '0') || 0,
            protein: parseFloat(selectedServingData.protein || '0') || 0,
            carbs: parseFloat(selectedServingData.carbohydrate || '0') || 0,
            fat: parseFloat(selectedServingData.fat || '0') || 0,
            fiber: parseFloat(selectedServingData.fiber || '0') || 0,
          };
          servingUnit =
            selectedServingData.measurement_description || 'serving';
          servingSize = parseFloat(selectedServingData.number_of_units || '1');
        }
      } else {
        throw new Error('Invalid food item');
      }

      // Create the food log entry with calculated macros from selected serving
      await createFoodLogEntry(
        foodItemId,
        mealType,
        macros,
        servingSize,
        servingUnit
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyLog'] });
      queryClient.invalidateQueries({ queryKey: ['summary'] });
      queryClient.invalidateQueries({ queryKey: ['timelineData'] });
      toast.success(`Food entry added to ${mealType}!`);
      setSelectedFoodItem(null); // Clear selection
      setFoodItemDetails(null);
      setSelectedServing(0);
      setFoodItemModalOpen(false); // Close modal
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
  });

  const handleSearch = () => {
    // Always allow search - empty query returns recently used items
    performSearch();
  };

  // Effect to fetch full details when an external food item is selected
  useEffect(() => {
    const loadDetails = async () => {
      if (
        foodItemModalOpen &&
        selectedFoodItem?.external_id &&
        selectedFoodItem?.external_provider &&
        !foodItemDetails
      ) {
        setSelectedServing(0); // Reset to first serving
        const result = await fetchDetails();
        if (result.data) {
          setFoodItemDetails(result.data);
        }
      }
    };

    loadDetails();
  }, [
    foodItemModalOpen,
    selectedFoodItem?.external_id,
    selectedFoodItem?.external_provider,
    foodItemDetails,
    fetchDetails,
  ]);

  const handleToggleFavorite = async (item: SearchResult) => {
    if (item.is_favorite) {
      // Find the favorite_id - since we don't have it in SearchResult,
      // we need to fetch favorites first or pass it differently
      // For now, we'll need to refetch to get the ID
      const favorites = await fetchFavorites();
      const favorite = favorites.find((fav: any) => {
        const favFoodItem = fav.food_item;
        if (item.id) {
          return favFoodItem.id === item.id;
        }
        return (
          favFoodItem.external_id === item.external_id &&
          favFoodItem.external_provider === item.external_provider
        );
      });

      if (favorite) {
        await removeFromFavoritesMutation.mutateAsync({
          favoriteId: favorite.id,
          item: item,
        });
      }
    } else {
      await addToFavoritesMutation.mutateAsync({
        foodItemId: item.id,
        externalId: item.external_id || null,
        externalProvider: item.external_provider || null,
      });
    }
  };

  return (
    <Container maxWidth="lg">
      <PageHeader title="NUTRITION TRACKING FLOW" />

      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add Food Log Entry
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Meal Type"
            value={mealType}
            onChange={(e) => setMealType(e.target.value)}
            options={[
              { value: 'breakfast', label: 'Breakfast' },
              { value: 'lunch', label: 'Lunch' },
              { value: 'dinner', label: 'Dinner' },
              { value: 'other', label: 'Other' },
              { value: 'snacks', label: 'Snacks' },
            ]}
            sx={{ minWidth: 200 }}
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button
            variant="prominent"
            onClick={() => addCustomEntryMutation.mutate()}
            disabled={addCustomEntryMutation.isPending}
          >
            {addCustomEntryMutation.isPending
              ? 'Adding...'
              : 'Add Custom Food (Dummy)'}
          </Button>

          <Button
            variant="bordered"
            color="primary"
            onClick={() => setQuickLogOpen(true)}
          >
            Quick Log
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column: Daily Log & Food Logs */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            {isLoading && <Typography>Loading daily log...</Typography>}

            {error && (
              <Typography color="error">
                Error: {(error as Error).message}
              </Typography>
            )}

            {!isLoading && !error && !dailyLog && (
              <Typography>No daily log found for today</Typography>
            )}

            {dailyLog && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 4,
                    p: 2,
                    border: '1px solid #ddd',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 3 }}>
                    Daily Log - {dailyLog.log_date}
                  </Typography>
                  <Typography variant="h6">TOTALS</Typography>
                  <Typography>
                    <strong>Total Calories:</strong> {dailyLog.total_calories}
                  </Typography>
                  <Typography>
                    <strong>Protein:</strong> {dailyLog.total_protein}g
                  </Typography>
                  <Typography>
                    <strong>Carbohydrates:</strong>{' '}
                    {dailyLog.total_carbohydrates}g
                  </Typography>
                  <Typography>
                    <strong>Fat:</strong> {dailyLog.total_fat}g
                  </Typography>
                  <Typography>
                    <strong>Fiber:</strong> {dailyLog.total_fiber}g
                  </Typography>
                </Box>

                <Typography variant="h6" sx={{ mb: 2 }}>
                  Food Logs:
                </Typography>
                {dailyLog?.food_logs?.map((foodLog) => (
                  <Box
                    key={foodLog.id}
                    sx={{
                      mb: 3,
                      p: 2,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    >
                      {foodLog.name} ({foodLog.meal_type})
                    </Typography>
                    {foodLog.food_log_entries.map((entry) => (
                      <Box key={entry.id} sx={{ ml: 2, mb: 2 }}>
                        {editingEntryId === entry.id ? (
                          // Edit mode
                          <Box
                            sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}
                          >
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>
                              Edit: {entry.food_item.name}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                              <TextField
                                label="Quantity"
                                type="number"
                                value={editQuantity}
                                onChange={(e) =>
                                  setEditQuantity(e.target.value)
                                }
                                sx={{ width: 120 }}
                                slotProps={{
                                  htmlInput: {
                                    step: 0.1,
                                    min: 0.1,
                                  },
                                }}
                              />
                              <TextField
                                label="Unit"
                                value={editUnit}
                                onChange={(e) => setEditUnit(e.target.value)}
                                sx={{ width: 150 }}
                              />
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="prominent"
                                size="small"
                                onClick={() => {
                                  updateEntryMutation.mutate({
                                    entryId: entry.id,
                                    quantity: parseFloat(editQuantity),
                                    unit: editUnit,
                                  });
                                }}
                                disabled={
                                  updateEntryMutation.isPending ||
                                  !editQuantity ||
                                  !editUnit
                                }
                              >
                                {updateEntryMutation.isPending
                                  ? 'Saving...'
                                  : 'Save'}
                              </Button>
                              <Button
                                variant="bordered"
                                size="small"
                                onClick={() => {
                                  setEditingEntryId(null);
                                  setEditQuantity('');
                                  setEditUnit('');
                                }}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          </Box>
                        ) : (
                          // View mode
                          <Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography>
                                  • {entry.food_item.name}
                                  {entry.food_item.brand &&
                                    ` (${entry.food_item.brand})`}
                                  {' - '}
                                  {entry.quantity} {entry.unit}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ ml: 2, color: 'text.secondary' }}
                                >
                                  Calories: {entry.calories} | Protein:{' '}
                                  {entry.protein}g | Carbs:{' '}
                                  {entry.carbohydrates}g | Fat: {entry.fat}g |
                                  Fiber: {entry.fiber}g
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ ml: 2, color: 'text.secondary' }}
                                >
                                  Source: {entry.food_item.source}
                                  {entry.food_item.external_id &&
                                    ` (ID: ${entry.food_item.external_id})`}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                                <Button
                                  variant="bordered"
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setEditingEntryId(entry.id);
                                    setEditQuantity(entry.quantity);
                                    setEditUnit(entry.unit);
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="bordered"
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    deleteEntryMutation.mutate(entry.id)
                                  }
                                  disabled={
                                    deleteEntryMutation.isPending &&
                                    deleteEntryMutation.variables === entry.id
                                  }
                                >
                                  {deleteEntryMutation.isPending &&
                                  deleteEntryMutation.variables === entry.id
                                    ? 'Deleting...'
                                    : 'Delete'}
                                </Button>
                              </Stack>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            )}

            {/* Summary Section */}
            <Box
              sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Summary</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label="Date Range"
                    value={summaryDateRange}
                    onChange={(e) => setSummaryDateRange(e.target.value)}
                    options={[
                      { value: 'today', label: 'Today' },
                      { value: 'this_week', label: 'This Week' },
                      { value: 'this_month', label: 'This Month' },
                      { value: 'last_3_months', label: 'Last 3 Months' },
                      { value: 'this_year', label: 'This Year' },
                    ]}
                    sx={{ minWidth: 150 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">View Remaining:</Typography>
                    <input
                      type="checkbox"
                      checked={viewRemaining}
                      onChange={(e) => setViewRemaining(e.target.checked)}
                    />
                  </Box>
                </Box>
              </Box>

              {isLoadingSummary && <Typography>Loading summary...</Typography>}

              {summaryError && (
                <Typography color="error">
                  Error: {(summaryError as Error).message}
                </Typography>
              )}

              {summary && (
                <Box>
                  <Box
                    sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      {viewRemaining ? 'Remaining Totals' : 'Current Totals'}
                    </Typography>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                    >
                      <Typography variant="caption">
                        Total Calories:{' '}
                        <strong>{summary.total_calories}</strong>
                      </Typography>
                      <Typography variant="caption">
                        Calories Goal: <strong>{summary.calories_goal}</strong>
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 'bold' }}
                  >
                    Macros Breakdown:
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    {/* Protein */}
                    <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                      >
                        Protein
                      </Typography>
                      <Typography variant="caption">
                        {viewRemaining ? 'Remaining' : 'Current'}:{' '}
                        {summary.macros.protein?.current}g
                      </Typography>
                      {summary.macros.protein?.goal !== undefined && (
                        <Typography variant="caption" sx={{ ml: 2 }}>
                          Goal: {summary.macros.protein?.goal}g
                        </Typography>
                      )}
                    </Box>

                    {/* Carbohydrates */}
                    <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                      >
                        Carbohydrates
                      </Typography>
                      <Typography variant="caption">
                        {viewRemaining ? 'Remaining' : 'Current'}:{' '}
                        {summary.macros.carbs?.current}g
                      </Typography>
                      {summary.macros.carbs?.goal !== undefined && (
                        <Typography variant="caption" sx={{ ml: 2 }}>
                          Goal: {summary.macros.carbs?.goal}g
                        </Typography>
                      )}
                    </Box>

                    {/* Fat */}
                    <Box sx={{ p: 1.5, bgcolor: '#f3e5f5', borderRadius: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                      >
                        Fat
                      </Typography>
                      <Typography variant="caption">
                        {viewRemaining ? 'Remaining' : 'Current'}:{' '}
                        {summary.macros.fat?.current}g
                      </Typography>
                      {summary.macros.fat?.goal !== undefined && (
                        <Typography variant="caption" sx={{ ml: 2 }}>
                          Goal: {summary.macros.fat?.goal}g
                        </Typography>
                      )}
                    </Box>

                    {/* Fiber */}
                    <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                      >
                        Fiber
                      </Typography>
                      <Typography variant="caption">
                        {viewRemaining ? 'Remaining' : 'Current'}:{' '}
                        {summary.macros.fiber?.current}g
                      </Typography>
                      {summary.macros.fiber?.goal !== undefined && (
                        <Typography variant="caption" sx={{ ml: 2 }}>
                          Goal: {summary.macros.fiber?.goal}g
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Weekly Summary (by_day_week) */}
            <Box
              sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  Weekly Pattern Summary (by Day of Week)
                </Typography>
                <TextField
                  label="Date Range"
                  value={weeklyDateRange}
                  onChange={(e) => setWeeklyDateRange(e.target.value)}
                  options={[
                    { value: 'this_week', label: 'This Week' },
                    { value: 'this_month', label: 'This Month' },
                    { value: 'last_3_months', label: 'Last 3 Months' },
                    { value: 'this_year', label: 'This Year' },
                  ]}
                  sx={{ minWidth: 150 }}
                />
              </Box>

              {isLoadingWeeklySummary && (
                <Typography>Loading weekly summary...</Typography>
              )}

              {weeklySummary && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    'monday',
                    'tuesday',
                    'wednesday',
                    'thursday',
                    'friday',
                    'saturday',
                    'sunday',
                  ].map((day) => {
                    const dayData = weeklySummary[day];
                    if (!dayData) return null;

                    return (
                      <Box
                        key={day}
                        sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 'bold',
                            mb: 1,
                            textTransform: 'capitalize',
                          }}
                        >
                          {day}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          <Typography variant="caption">
                            Calories:{' '}
                            <strong>{dayData.calories.current}</strong>
                          </Typography>
                          <Typography variant="caption">
                            Protein: <strong>{dayData.protein.current}g</strong>
                          </Typography>
                          <Typography variant="caption">
                            Carbs: <strong>{dayData.carbs.current}g</strong>
                          </Typography>
                          <Typography variant="caption">
                            Fat: <strong>{dayData.fat.current}g</strong>
                          </Typography>
                          <Typography variant="caption">
                            Fiber: <strong>{dayData.fiber.current}g</strong>
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Daily Summary (by_date) */}
            <Box
              sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  Daily Trend Summary (by Date)
                </Typography>
                <TextField
                  label="Date Range"
                  value={dailyDateRange}
                  onChange={(e) => setDailyDateRange(e.target.value)}
                  options={[
                    { value: 'this_week', label: 'This Week' },
                    { value: 'this_month', label: 'This Month' },
                    { value: 'last_3_months', label: 'Last 3 Months' },
                    { value: 'this_year', label: 'This Year' },
                  ]}
                  sx={{ minWidth: 150 }}
                />
              </Box>

              {isLoadingDailySummary && (
                <Typography>Loading daily summary...</Typography>
              )}

              {dailySummary && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    maxHeight: '400px',
                    overflowY: 'auto',
                  }}
                >
                  {Object.entries(dailySummary).map(([date, dayData]) => (
                    <Box
                      key={date}
                      sx={{ p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                      >
                        {date}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption">
                          Cal: <strong>{dayData.calories.current}</strong>
                        </Typography>
                        <Typography variant="caption">
                          P: <strong>{dayData.protein.current}g</strong>
                        </Typography>
                        <Typography variant="caption">
                          C: <strong>{dayData.carbs.current}g</strong>
                        </Typography>
                        <Typography variant="caption">
                          F: <strong>{dayData.fat.current}g</strong>
                        </Typography>
                        <Typography variant="caption">
                          Fiber: <strong>{dayData.fiber.current}g</strong>
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Timeline Chart Section */}
            <Box
              sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Timeline Chart</Typography>
                <TextField
                  label="Date Range"
                  value={timelineDateRange}
                  onChange={(e) => setTimelineDateRange(e.target.value)}
                  options={[
                    { value: 'this_week', label: 'This Week' },
                    { value: 'this_month', label: 'This Month' },
                    { value: 'last_3_months', label: 'Last 3 Months' },
                    { value: 'this_year', label: 'This Year' },
                  ]}
                  sx={{ minWidth: 150 }}
                />
              </Box>

              {/* Comparison Filter Buttons */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  variant={
                    timelineComparison === null ? 'prominent' : 'bordered'
                  }
                  size="small"
                  onClick={() => setTimelineComparison(null)}
                >
                  Both
                </Button>
                <Button
                  variant={
                    timelineComparison === 'intake' ? 'prominent' : 'bordered'
                  }
                  size="small"
                  onClick={() => setTimelineComparison('intake')}
                >
                  Intake Only
                </Button>
                <Button
                  variant={
                    timelineComparison === 'target' ? 'prominent' : 'bordered'
                  }
                  size="small"
                  onClick={() => setTimelineComparison('target')}
                >
                  Target Only
                </Button>
              </Stack>

              {isLoadingTimeline && (
                <Typography>Loading timeline...</Typography>
              )}

              {timelineData && timelineData.length > 0 && (
                <Box>
                  {/* Calories Timeline Chart */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Calories Over Time
                    </Typography>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={timelineData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="log_date"
                          tick={{ fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {(!timelineComparison ||
                          timelineComparison === 'intake') && (
                          <Line
                            type="monotone"
                            dataKey="intake.calories"
                            stroke="#8884d8"
                            name="Intake"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        )}
                        {(!timelineComparison ||
                          timelineComparison === 'target') && (
                          <Line
                            type="monotone"
                            dataKey="target.calories"
                            stroke="#82ca9d"
                            name="Target"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Protein Timeline Chart */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Protein Over Time (g)
                    </Typography>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={timelineData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="log_date"
                          tick={{ fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {(!timelineComparison ||
                          timelineComparison === 'intake') && (
                          <Line
                            type="monotone"
                            dataKey="intake.protein"
                            stroke="#ff7300"
                            name="Intake"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        )}
                        {(!timelineComparison ||
                          timelineComparison === 'target') && (
                          <Line
                            type="monotone"
                            dataKey="target.protein"
                            stroke="#387908"
                            name="Target"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Raw Timeline Data (for debugging) */}
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Raw Timeline Data:
                    </Typography>
                    <pre
                      style={{
                        fontSize: '10px',
                        backgroundColor: '#f5f5f5',
                        padding: '8px',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(timelineData, null, 2)}
                    </pre>
                  </Box>
                </Box>
              )}

              {timelineData && timelineData.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No timeline data available for the selected range.
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Right Column: Search */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Search Food Items
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                label="Search"
                placeholder="Search for food items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                fullWidth
              />
              <Button
                variant="prominent"
                onClick={handleSearch}
                disabled={isSearching}
              >
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
            </Stack>

            {/* Filter Buttons */}
            <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
              <Button
                variant={favoritesOnly ? 'prominent' : 'bordered'}
                size="small"
                onClick={() => {
                  setFavoritesOnly(!favoritesOnly);
                  if (!favoritesOnly) {
                    setCustomOnly(false); // Disable custom only when enabling favorites
                  }
                }}
                sx={{ minWidth: 120 }}
              >
                ⭐ Favorites
              </Button>
              <Button
                variant={customOnly ? 'prominent' : 'bordered'}
                size="small"
                onClick={() => {
                  setCustomOnly(!customOnly);
                  if (!customOnly) {
                    setFavoritesOnly(false); // Disable favorites when enabling custom
                  }
                }}
                sx={{ minWidth: 140 }}
              >
                My Custom Foods
              </Button>
              {(favoritesOnly || customOnly) && (
                <Button
                  variant="borderless"
                  size="small"
                  onClick={() => {
                    setFavoritesOnly(false);
                    setCustomOnly(false);
                  }}
                  sx={{ minWidth: 80 }}
                >
                  Clear Filters
                </Button>
              )}
            </Stack>

            {isSearching && <Typography>Searching...</Typography>}

            {searchResults && searchResults.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Results ({searchResults.length}):
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mb: 3,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    pr: 1,
                  }}
                >
                  {searchResults.map((item) => {
                    // Use external_id for external items, id for custom items
                    const itemKey = item.external_id || item.id;

                    return (
                      <Box
                        key={itemKey}
                        sx={{
                          p: 1.5,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          border: '2px solid',
                          borderColor: 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          position: 'relative',
                          '&:hover': {
                            bgcolor: '#f5f5f5',
                            borderColor: '#e0e0e0',
                          },
                        }}
                      >
                        {/* Favorite Star Button */}
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(item);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            lineHeight: 1,
                            userSelect: 'none',
                            '&:hover': {
                              transform: 'scale(1.2)',
                            },
                            transition: 'transform 0.2s',
                          }}
                        >
                          {item.is_favorite ? '⭐' : '☆'}
                        </Box>

                        <Box
                          onClick={() => {
                            // eslint-disable-next-line no-console
                            console.log('Selected item:', item);
                            setSelectedFoodItem(item);
                            setFoodItemModalOpen(true);
                            setSelectedServing(0); // Reset serving selection

                            // If it's an external item, fetch full details
                            if (item.external_id && item.external_provider) {
                              setFoodItemDetails(null); // Clear previous details
                              // Fetch will be triggered by useEffect
                            } else {
                              // For custom items, use the item directly
                              setFoodItemDetails(item);
                            }
                          }}
                          sx={{ pr: 4 }}
                        >
                          <Typography variant="body1">{item.name}</Typography>
                          {item.brand && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.brand}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            sx={{ ml: 1, color: 'primary.main' }}
                          >
                            ({item.source})
                          </Typography>

                          {/* Nutrition Summary */}
                          {item.nutrition_summary &&
                            Object.keys(item.nutrition_summary).length > 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {item.nutrition_summary.serving_description &&
                                  `${item.nutrition_summary.serving_description} - `}
                                Calories:{' '}
                                {item.nutrition_summary.calories || '0'}kcal
                                {' | '}
                                Protein: {item.nutrition_summary.protein || '0'}
                                g{' | '}
                                Carbs: {item.nutrition_summary.carbs || '0'}g
                                {' | '}
                                Fat: {item.nutrition_summary.fat || '0'}g
                                {item.nutrition_summary.fiber &&
                                  ` | Fiber: ${item.nutrition_summary.fiber}g`}
                              </Typography>
                            )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {searchResults && searchResults.length === 0 && (
              <Typography color="text.secondary">No results found</Typography>
            )}
          </Box>

          {/* Goals Section - Only for Trainers */}
          {/* My Rest Days Section */}
          <Box
            sx={{
              mt: 4,
              p: 2,
              border: '2px solid #f44336',
              borderRadius: 1,
              bgcolor: '#ffebee',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: '#f44336' }}>
              My Rest Days
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Mark which days of the week are rest days (informational only,
              does not affect goal resolution)
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
              {DAY_NAMES.map((day, idx) => {
                const currentRestDays =
                  editingRestDays ?? userNutritionSettings?.rest_days ?? [];
                const isSelected = currentRestDays.includes(idx);

                return (
                  <Box
                    key={idx}
                    onClick={() => {
                      const newRestDays = isSelected
                        ? currentRestDays.filter((d: number) => d !== idx)
                        : [...currentRestDays, idx];
                      setEditingRestDays(newRestDays);
                    }}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: isSelected ? '#f44336' : '#e0e0e0',
                      color: isSelected ? 'white' : 'text.primary',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    {day}
                  </Box>
                );
              })}
            </Box>

            {editingRestDays !== null && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="prominent"
                  size="small"
                  color="error"
                  onClick={() => {
                    updateUserNutritionSettingsMutation.mutate({
                      rest_days: editingRestDays,
                    });
                  }}
                >
                  Save My Rest Days
                </Button>
                <Button
                  variant="bordered"
                  size="small"
                  onClick={() => setEditingRestDays(null)}
                >
                  Cancel
                </Button>
              </Stack>
            )}
          </Box>

          {/* Goals Schedule Section */}
          <Box sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              My Goals Schedule
            </Typography>

            {isLoadingUserSettings && <Typography>Loading...</Typography>}

            {!isLoadingUserSettings && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Assign a nutrition goal to each day of the week. Click Edit to
                  make changes.
                </Typography>

                {editingGoalsSchedule !== null ? (
                  // Edit mode
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      {DAY_NAMES.map((dayName, dayIndex) => {
                        const currentSchedule = editingGoalsSchedule || [];
                        const dayEntry = currentSchedule.find(
                          (entry) => entry.day_of_week === dayIndex
                        );
                        const selectedGoalId = dayEntry?.goal_id || '';

                        return (
                          <Box
                            key={dayIndex}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                minWidth: 100,
                                fontWeight: 'bold',
                              }}
                            >
                              {dayName}:
                            </Typography>
                            <TextField
                              value={selectedGoalId}
                              onChange={(e) => {
                                const goalId = e.target.value
                                  ? Number(e.target.value)
                                  : null;
                                const selectedGoal = goalId
                                  ? goals?.find((g) => g.id === goalId)
                                  : null;

                                let newSchedule = [...currentSchedule];
                                newSchedule = newSchedule.filter(
                                  (entry) => entry.day_of_week !== dayIndex
                                );

                                if (goalId && selectedGoal) {
                                  newSchedule.push({
                                    day_of_week: dayIndex,
                                    goal_id: goalId,
                                    name: selectedGoal.name,
                                    input_mode: selectedGoal.input_mode,
                                    meta: selectedGoal.meta,
                                  });
                                }

                                setEditingGoalsSchedule(newSchedule);
                              }}
                              size="small"
                              fullWidth
                              options={[
                                { value: '', label: 'No goal' },
                                ...(goals?.map((goal) => ({
                                  value: goal.id,
                                  label: goal.name,
                                })) || []),
                              ]}
                            />
                          </Box>
                        );
                      })}
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="prominent"
                        size="small"
                        color="primary"
                        onClick={() => {
                          updateUserNutritionSettingsMutation.mutate({
                            nutrition_goals_schedule:
                              editingGoalsSchedule || [],
                          });
                        }}
                      >
                        Save Goals Schedule
                      </Button>
                      <Button
                        variant="bordered"
                        size="small"
                        onClick={() => setEditingGoalsSchedule(null)}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </>
                ) : (
                  // View mode
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      {DAY_NAMES.map((dayName, dayIndex) => {
                        const schedule =
                          userNutritionSettings?.nutrition_goals_schedule || [];
                        const dayEntry = schedule.find(
                          (entry: any) => Number(entry.day_of_week) === dayIndex
                        );

                        return (
                          <Box
                            key={dayIndex}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 1,
                              bgcolor: dayEntry?.name ? '#e8f5e9' : '#f5f5f5',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                minWidth: 100,
                                fontWeight: 'bold',
                              }}
                            >
                              {dayName}:
                            </Typography>
                            <Typography variant="body2">
                              {dayEntry?.name || 'No goal assigned'}
                            </Typography>
                            {dayEntry?.meta?.calories && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                ({dayEntry.meta.calories} cal)
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>

                    <Button
                      variant="bordered"
                      size="small"
                      onClick={() => {
                        setEditingGoalsSchedule(
                          userNutritionSettings?.nutrition_goals_schedule || []
                        );
                      }}
                    >
                      Edit Schedule
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>

          {/* Nutrition Goal Settings (User-Level) */}
          {isTrainer(user) && (
            <Box
              sx={{
                mt: 4,
                p: 2,
                border: '1px solid #9c27b0',
                borderRadius: 1,
                bgcolor: '#f3e5f5',
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, color: '#9c27b0' }}>
                Nutrition Goal Settings
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                These settings apply globally to the user, regardless of which
                goal is assigned.
              </Typography>

              {isLoadingUserSettings ? (
                <Typography>Loading...</Typography>
              ) : editingGoalSettings ? (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    {[
                      {
                        key: 'enable_fiber_goal' as const,
                        label: 'Enable Fiber Goal',
                      },
                      {
                        key: 'enable_rest_day_goals' as const,
                        label: 'Enable Rest Day Goals',
                      },
                      {
                        key: 'track_net_carbs' as const,
                        label: 'Track Net Carbs',
                      },
                      {
                        key: 'customize_calories_per_gram' as const,
                        label: 'Customize Calories Per Gram',
                      },
                      {
                        key: 'enable_custom_day_goal_schedule' as const,
                        label: 'Enable Custom Day Goal Schedule',
                      },
                    ].map(({ key, label }) => (
                      <Typography
                        key={key}
                        variant="caption"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            (editGoalSettingsData[key] ??
                              userNutritionSettings?.nutrition_goal_settings?.[
                                key
                              ] ??
                              false) as boolean
                          }
                          onChange={(e) =>
                            setEditGoalSettingsData({
                              ...editGoalSettingsData,
                              [key]: e.target.checked,
                            })
                          }
                        />{' '}
                        {label}
                      </Typography>
                    ))}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
                    >
                      Track Net Carbs Options:
                    </Typography>
                    {['fiber', 'sugar_alcohols', 'allulose', 'fructose'].map(
                      (option) => {
                        const currentArray =
                          editGoalSettingsData.track_net_carbs_array ??
                          userNutritionSettings?.nutrition_goal_settings
                            ?.track_net_carbs_array ??
                          [];
                        const isChecked = currentArray.includes(option);

                        return (
                          <Typography
                            key={option}
                            variant="caption"
                            sx={{ display: 'block', ml: 2 }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const newArray = e.target.checked
                                  ? [...currentArray, option]
                                  : currentArray.filter(
                                      (item: string) => item !== option
                                    );
                                setEditGoalSettingsData({
                                  ...editGoalSettingsData,
                                  track_net_carbs_array: newArray,
                                });
                              }}
                            />{' '}
                            {option.replace(/_/g, ' ')}
                          </Typography>
                        );
                      }
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
                    >
                      Customized Calories (cal/g):
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flexWrap: 'wrap' }}
                    >
                      {(['protein', 'carbs', 'fat'] as const).map((macro) => (
                        <TextField
                          key={macro}
                          label={macro.charAt(0).toUpperCase() + macro.slice(1)}
                          type="number"
                          value={
                            editGoalSettingsData.customized_calories?.[macro] ??
                            userNutritionSettings?.nutrition_goal_settings
                              ?.customized_calories?.[macro] ??
                            ''
                          }
                          onChange={(e) =>
                            setEditGoalSettingsData({
                              ...editGoalSettingsData,
                              customized_calories: {
                                ...editGoalSettingsData.customized_calories,
                                [macro]: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          sx={{ width: 100 }}
                          slotProps={{
                            htmlInput: { step: 0.1, min: 0 },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="prominent"
                      size="small"
                      color="neutral"
                      onClick={() => {
                        updateUserNutritionSettingsMutation.mutate({
                          nutrition_goal_settings: editGoalSettingsData,
                        });
                      }}
                    >
                      Save Settings
                    </Button>
                    <Button
                      variant="bordered"
                      size="small"
                      onClick={() => {
                        setEditingGoalSettings(false);
                        setEditGoalSettingsData({});
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      mb: 2,
                    }}
                  >
                    <Typography variant="caption">
                      Fiber Goal:{' '}
                      {userNutritionSettings?.nutrition_goal_settings
                        ?.enable_fiber_goal
                        ? 'Enabled'
                        : 'Disabled'}
                    </Typography>
                    <Typography variant="caption">
                      Rest Day Goals:{' '}
                      {userNutritionSettings?.nutrition_goal_settings
                        ?.enable_rest_day_goals
                        ? 'Enabled'
                        : 'Disabled'}
                    </Typography>
                    <Typography variant="caption">
                      Track Net Carbs:{' '}
                      {userNutritionSettings?.nutrition_goal_settings
                        ?.track_net_carbs
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                    <Typography variant="caption">
                      Customize Cal/g:{' '}
                      {userNutritionSettings?.nutrition_goal_settings
                        ?.customize_calories_per_gram
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                    <Typography variant="caption">
                      Custom Day Schedule:{' '}
                      {userNutritionSettings?.nutrition_goal_settings
                        ?.enable_custom_day_goal_schedule
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                    {userNutritionSettings?.nutrition_goal_settings
                      ?.track_net_carbs_array &&
                      userNutritionSettings.nutrition_goal_settings
                        .track_net_carbs_array.length > 0 && (
                        <Typography variant="caption">
                          Net Carbs Array:{' '}
                          {userNutritionSettings.nutrition_goal_settings.track_net_carbs_array.join(
                            ', '
                          )}
                        </Typography>
                      )}
                    {userNutritionSettings?.nutrition_goal_settings
                      ?.customized_calories &&
                      Object.keys(
                        userNutritionSettings.nutrition_goal_settings
                          .customized_calories
                      ).length > 0 && (
                        <Typography variant="caption">
                          Custom Cal/g: Protein=
                          {
                            userNutritionSettings.nutrition_goal_settings
                              .customized_calories.protein
                          }
                          , Carbs=
                          {
                            userNutritionSettings.nutrition_goal_settings
                              .customized_calories.carbs
                          }
                          , Fat=
                          {
                            userNutritionSettings.nutrition_goal_settings
                              .customized_calories.fat
                          }
                        </Typography>
                      )}
                  </Box>
                  <Button
                    variant="bordered"
                    size="small"
                    color="neutral"
                    onClick={() => {
                      setEditingGoalSettings(true);
                      setEditGoalSettingsData({});
                    }}
                  >
                    Edit Goal Settings
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* My Current Goal Section */}
          <Box sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              My Current Goal
            </Typography>

            {isLoadingCurrentGoal && (
              <Typography>Loading current goal...</Typography>
            )}

            {currentGoal?.goal ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#e8f5e9',
                  borderRadius: 1,
                  border: '1px solid #4caf50',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Date: {currentGoal.date}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', mb: 1 }}
                >
                  {currentGoal.goal.name}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Input Mode: {currentGoal.goal.input_mode}
                </Typography>

                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
                  >
                    Targets:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2">
                      <strong>Calories:</strong>{' '}
                      {currentGoal.goal.meta?.calories || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Protein:</strong>{' '}
                      {currentGoal.goal.meta?.protein || 'N/A'}g
                    </Typography>
                    <Typography variant="body2">
                      <strong>Carbs:</strong>{' '}
                      {currentGoal.goal.meta?.carbs || 'N/A'}g
                    </Typography>
                    <Typography variant="body2">
                      <strong>Fat:</strong>{' '}
                      {currentGoal.goal.meta?.fat || 'N/A'}g
                    </Typography>
                    <Typography variant="body2">
                      <strong>Fiber:</strong>{' '}
                      {currentGoal.goal.meta?.fiber || 'N/A'}g
                    </Typography>
                  </Box>
                </Box>

                {currentGoal.nutrition_goal_settings &&
                  Object.keys(currentGoal.nutrition_goal_settings).length >
                    0 && (
                    <Box sx={{ mt: 2, pt: 1, borderTop: '1px dashed #ccc' }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                      >
                        Active Settings:
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        {currentGoal.nutrition_goal_settings
                          .enable_fiber_goal && (
                          <Typography variant="caption">
                            Fiber Goal ON
                          </Typography>
                        )}
                        {currentGoal.nutrition_goal_settings
                          .track_net_carbs && (
                          <Typography variant="caption">
                            Net Carbs ON
                          </Typography>
                        )}
                        {currentGoal.nutrition_goal_settings
                          .customize_calories_per_gram && (
                          <Typography variant="caption">
                            Custom Cal/g ON
                          </Typography>
                        )}
                        {currentGoal.nutrition_goal_settings
                          .enable_custom_day_goal_schedule && (
                          <Typography variant="caption">
                            Custom Day Schedule ON
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
              </Box>
            ) : (
              <Typography color="text.secondary">
                {currentGoal?.message ||
                  'No current goal assigned for today.'}{' '}
                {isTrainer(user)
                  ? 'Create a goal template and assign it in the Goals Schedule section.'
                  : 'Contact your trainer to set up a goal.'}
              </Typography>
            )}
          </Box>

          {/* Nutrition Goal Templates Section (Trainers only) */}
          {isTrainer(user) && (
            <Box
              sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Goal Templates
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Goals are templates. When assigned via the schedule, their
                values are copied to the client. Editing a template here does
                not affect existing assignments.
              </Typography>

              {isLoadingGoals && <Typography>Loading goals...</Typography>}

              {goals && goals.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {goals.map((goal) => (
                    <Box
                      key={goal.id}
                      sx={{
                        p: 2,
                        bgcolor: '#e3f2fd',
                        borderRadius: 1,
                        border: '1px solid #1976d2',
                      }}
                    >
                      {editingGoalId === goal.id ? (
                        // Edit mode
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            Edit Goal
                          </Typography>
                          <TextField
                            label="Name"
                            value={editGoalName}
                            onChange={(e) => setEditGoalName(e.target.value)}
                            fullWidth
                            sx={{ mb: 2 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ mb: 1, display: 'block' }}
                          >
                            Targets (in grams):
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{ mb: 2, flexWrap: 'wrap' }}
                          >
                            <TextField
                              label="Calories"
                              type="number"
                              value={editGoalMetadata.calories || ''}
                              onChange={(e) =>
                                setEditGoalMetadata({
                                  ...editGoalMetadata,
                                  calories: parseFloat(e.target.value) || 0,
                                })
                              }
                              sx={{ width: 120 }}
                            />
                            <TextField
                              label="Protein (g)"
                              type="number"
                              value={editGoalMetadata.protein || ''}
                              onChange={(e) =>
                                setEditGoalMetadata({
                                  ...editGoalMetadata,
                                  protein: parseFloat(e.target.value) || 0,
                                })
                              }
                              sx={{ width: 120 }}
                            />
                            <TextField
                              label="Carbs (g)"
                              type="number"
                              value={editGoalMetadata.carbs || ''}
                              onChange={(e) =>
                                setEditGoalMetadata({
                                  ...editGoalMetadata,
                                  carbs: parseFloat(e.target.value) || 0,
                                })
                              }
                              sx={{ width: 120 }}
                            />
                            <TextField
                              label="Fat (g)"
                              type="number"
                              value={editGoalMetadata.fat || ''}
                              onChange={(e) =>
                                setEditGoalMetadata({
                                  ...editGoalMetadata,
                                  fat: parseFloat(e.target.value) || 0,
                                })
                              }
                              sx={{ width: 120 }}
                            />
                            <TextField
                              label="Fiber (g)"
                              type="number"
                              value={editGoalMetadata.fiber || ''}
                              onChange={(e) =>
                                setEditGoalMetadata({
                                  ...editGoalMetadata,
                                  fiber: parseFloat(e.target.value) || 0,
                                })
                              }
                              sx={{ width: 120 }}
                            />
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="prominent"
                              size="small"
                              onClick={() => {
                                updateGoalMutation.mutate({
                                  id: goal.id,
                                  data: {
                                    name: editGoalName,
                                    meta: editGoalMetadata,
                                  },
                                });
                                setEditingGoalId(null);
                                setEditGoalName('');
                                setEditGoalMetadata({});
                              }}
                              disabled={!editGoalName}
                            >
                              Save
                            </Button>
                            <Button
                              variant="bordered"
                              size="small"
                              onClick={() => {
                                setEditingGoalId(null);
                                setEditGoalName('');
                                setEditGoalMetadata({});
                              }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Box>
                      ) : (
                        // View mode
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {goal.name}
                            </Typography>
                            {isTrainer(user) && (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="bordered"
                                  size="small"
                                  onClick={() => {
                                    setEditingGoalId(goal.id);
                                    setEditGoalName(goal.name);
                                    setEditGoalMetadata(goal.meta || {});
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="bordered"
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    deleteGoalMutation.mutate(goal.id)
                                  }
                                >
                                  Delete
                                </Button>
                              </Stack>
                            )}
                          </Box>

                          <Typography variant="body2" color="text.secondary">
                            Input Mode: {goal.input_mode}
                          </Typography>

                          {goal.meta && Object.keys(goal.meta).length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 'bold' }}
                              >
                                Targets:
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  flexWrap: 'wrap',
                                  mt: 0.5,
                                }}
                              >
                                {goal.meta.calories && (
                                  <Typography variant="caption">
                                    Calories: {goal.meta.calories}
                                  </Typography>
                                )}
                                {goal.meta.protein && (
                                  <Typography variant="caption">
                                    Protein: {goal.meta.protein}g
                                  </Typography>
                                )}
                                {goal.meta.carbs && (
                                  <Typography variant="caption">
                                    Carbs: {goal.meta.carbs}g
                                  </Typography>
                                )}
                                {goal.meta.fat && (
                                  <Typography variant="caption">
                                    Fat: {goal.meta.fat}g
                                  </Typography>
                                )}
                                {goal.meta.fiber && (
                                  <Typography variant="caption">
                                    Fiber: {goal.meta.fiber}g
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
                          >
                            Template - assign via schedule to apply
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                !isLoadingGoals && (
                  <Typography color="text.secondary">
                    No goal templates yet. Create your first one!
                  </Typography>
                )
              )}

              {isTrainer(user) && (
                <Button
                  variant="prominent"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    const dummyGoal = {
                      name: `Goal ${Date.now()}`,
                      input_mode: 'grams', // Valid options: 'grams' or 'percentage'
                      meta: {
                        calories: 2000,
                        protein: 150,
                        carbs: 200,
                        fat: 65,
                        fiber: 25,
                      },
                    };
                    createGoalMutation.mutate(dummyGoal);
                  }}
                >
                  Create Dummy Goal
                </Button>
              )}
            </Box>
          )}

          {/* Nutrition Notes Section */}
          <Box sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Daily Notes
            </Typography>

            {/* Client Selector - Only shown for trainers */}
            {isTrainer(user) && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Create note for"
                  value={selectedClientId ?? ''}
                  onChange={(e) =>
                    setSelectedClientId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  options={[
                    { value: '', label: 'Myself' },
                    ...(Array.isArray(clients)
                      ? clients.map((client) => ({
                          value: client.id,
                          label: `${client.first_name} ${client.last_name} (${client.email})`,
                        }))
                      : []),
                  ]}
                  fullWidth
                  disabled={isLoadingClients}
                />
                {selectedClientId && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Note will be created for selected client
                  </Typography>
                )}
              </Box>
            )}

            {!isAddingNote && !editingNoteId && (
              <Button
                variant="prominent"
                onClick={() => setIsAddingNote(true)}
                sx={{ mb: 2 }}
              >
                Add Note
              </Button>
            )}

            {/* Add/Edit Note Form */}
            {(isAddingNote || editingNoteId) && (
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {editingNoteId ? 'Edit Note' : 'Add New Note'}
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    label="Title (optional)"
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                    fullWidth
                  />

                  <TextField
                    label="Notes"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    multiline
                    rows={4}
                    fullWidth
                  />

                  <Box>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="note-image-upload"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setNoteImage(file);
                          setNoteImagePreview(URL.createObjectURL(file));
                        }
                      }}
                    />
                    <label htmlFor="note-image-upload">
                      <Button variant="bordered" component="span">
                        {noteImage ? 'Change Image' : 'Add Image (optional)'}
                      </Button>
                    </label>

                    {noteImagePreview && (
                      <Box sx={{ mt: 2 }}>
                        <Box
                          component="img"
                          src={noteImagePreview}
                          alt="Preview"
                          sx={{ maxWidth: 200, borderRadius: 1 }}
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setNoteImage(null);
                            setNoteImagePreview(null);
                            if (editingNoteId && existingImageUrl) {
                              // Mark for removal if editing and had existing image
                              setShouldRemoveImage(true);
                            }
                          }}
                          sx={{ ml: 2 }}
                        >
                          Remove
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="prominent"
                      onClick={() => {
                        if (editingNoteId) {
                          updateNoteMutation.mutate({
                            id: editingNoteId,
                            data: {
                              title: noteTitle,
                              notes: noteText,
                              image: noteImage || undefined,
                              removeImage: shouldRemoveImage,
                            },
                          });
                        } else {
                          createNoteMutation.mutate({
                            title: noteTitle,
                            notes: noteText,
                            date: new Date(),
                            user_id: selectedClientId || Number(user?.id) || 0,
                            image: noteImage || undefined,
                          });
                        }
                      }}
                      disabled={
                        !noteText ||
                        createNoteMutation.isPending ||
                        updateNoteMutation.isPending
                      }
                    >
                      {editingNoteId
                        ? updateNoteMutation.isPending
                          ? 'Updating...'
                          : 'Update Note'
                        : createNoteMutation.isPending
                          ? 'Adding...'
                          : 'Add Note'}
                    </Button>
                    <Button
                      variant="bordered"
                      onClick={() => {
                        setIsAddingNote(false);
                        setEditingNoteId(null);
                        setNoteTitle('');
                        setNoteText('');
                        setNoteImage(null);
                        setNoteImagePreview(null);
                        setShouldRemoveImage(false);
                        setExistingImageUrl(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* Notes List */}
            {isLoadingNotes && <Typography>Loading notes...</Typography>}

            {nutritionNotes && nutritionNotes.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {nutritionNotes.map((note) => (
                  <Box
                    key={note.id}
                    sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}
                  >
                    {note.title && (
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold', mb: 1 }}
                      >
                        {note.title}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, whiteSpace: 'pre-wrap' }}
                    >
                      {note.notes}
                    </Typography>
                    {note.image && (
                      <Box
                        component="img"
                        src={note.image.thumb_url}
                        alt={note.image.filename}
                        sx={{
                          maxWidth: 200,
                          borderRadius: 1,
                          mb: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(note.image?.url, '_blank')}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {new Date(note.date * 1000).toLocaleString()}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="bordered"
                        size="small"
                        onClick={() => {
                          setEditingNoteId(note.id);
                          setNoteTitle(note.title);
                          setNoteText(note.notes);
                          setShouldRemoveImage(false);
                          if (note.image) {
                            setNoteImagePreview(note.image.thumb_url);
                            setExistingImageUrl(note.image.url);
                          } else {
                            setNoteImagePreview(null);
                            setExistingImageUrl(null);
                          }
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="bordered"
                        size="small"
                        color="error"
                        onClick={() => {
                          if (
                            confirm(
                              'Are you sure you want to delete this note?'
                            )
                          ) {
                            deleteNoteMutation.mutate(note.id);
                          }
                        }}
                        disabled={deleteNoteMutation.isPending}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              !isLoadingNotes &&
              !isAddingNote && (
                <Typography color="text.secondary">
                  No notes yet. Add your first nutrition note!
                </Typography>
              )
            )}
          </Box>

          {/* Weight Tracking Section */}
          <Box sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Weight Tracking
            </Typography>

            {/* Add New Weight */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Log New Weight
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Weight"
                  type="number"
                  value={newWeight}
                  onChange={(e) => setNewWeight(e.target.value)}
                  placeholder="Enter weight..."
                  slotProps={{
                    htmlInput: {
                      step: 0.1,
                      min: 0,
                    },
                  }}
                  fullWidth
                />
                <TextField
                  label="Note (optional)"
                  value={newWeightNote}
                  onChange={(e) => setNewWeightNote(e.target.value)}
                  placeholder="Add a note about this weight entry..."
                  multiline
                  rows={2}
                  fullWidth
                />
                <Button
                  variant="prominent"
                  onClick={() => {
                    const weight = parseFloat(newWeight);
                    if (!isNaN(weight) && weight > 0) {
                      createWeightMutation.mutate({
                        weight,
                        note: newWeightNote || undefined,
                      });
                    }
                  }}
                  disabled={
                    createWeightMutation.isPending ||
                    !newWeight ||
                    parseFloat(newWeight) <= 0
                  }
                >
                  {createWeightMutation.isPending ? 'Logging...' : 'Log Weight'}
                </Button>
              </Stack>
            </Box>

            {/* Weight History */}
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Weight History (Last 90 Days)
            </Typography>

            {isLoadingWeightStats && (
              <Typography>Loading weight history...</Typography>
            )}

            {weightStats && weightStats.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}
              >
                {weightStats
                  .sort((a, b) => b.created_at - a.created_at) // Most recent first
                  .map((stat, index) => {
                    const date = new Date(stat.created_at * 1000);
                    const formattedDate = date.toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    });

                    return (
                      <Box
                        key={`${stat.created_at}-${index}`}
                        sx={{
                          p: 2,
                          bgcolor: index === 0 ? '#e3f2fd' : '#f9f9f9',
                          borderRadius: 1,
                          border:
                            index === 0
                              ? '2px solid #1976d2'
                              : '1px solid #e0e0e0',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: stat.nutrition_tracking_note ? 1 : 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {stat.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {stat.metric || 'lb'}
                            </Typography>
                            {index === 0 && (
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  bgcolor: '#4caf50',
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  ml: 1,
                                }}
                              >
                                LATEST
                              </Box>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {formattedDate}
                          </Typography>
                        </Box>
                        {stat.nutrition_tracking_note && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1,
                              bgcolor: 'rgba(0,0,0,0.03)',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: 'italic',
                                color: 'text.secondary',
                              }}
                            >
                              📝 {stat.nutrition_tracking_note}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
              </Box>
            ) : (
              !isLoadingWeightStats && (
                <Typography color="text.secondary">
                  No weight entries yet. Log your first weight above!
                </Typography>
              )
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Quick Log Dialog */}
      <Dialog
        open={quickLogOpen}
        onClose={() => setQuickLogOpen(false)}
        title={`Quick Log - ${mealType.charAt(0).toUpperCase() + mealType.slice(1)}`}
        maxWidth="sm"
        fullWidth
      >
        <Stack spacing={3} sx={{ pt: 2 }}>
          <TextField
            label="Calories *"
            type="number"
            value={quickLogMacros.calories}
            onChange={(e) =>
              setQuickLogMacros({ ...quickLogMacros, calories: e.target.value })
            }
            fullWidth
            required
            InputProps={{ inputProps: { min: 0, step: 1 } }}
          />

          <TextField
            label="Protein (g)"
            type="number"
            value={quickLogMacros.protein}
            onChange={(e) =>
              setQuickLogMacros({ ...quickLogMacros, protein: e.target.value })
            }
            fullWidth
            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
          />

          <TextField
            label="Carbs (g)"
            type="number"
            value={quickLogMacros.carbs}
            onChange={(e) =>
              setQuickLogMacros({ ...quickLogMacros, carbs: e.target.value })
            }
            fullWidth
            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
          />

          <TextField
            label="Fat (g)"
            type="number"
            value={quickLogMacros.fat}
            onChange={(e) =>
              setQuickLogMacros({ ...quickLogMacros, fat: e.target.value })
            }
            fullWidth
            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
          />

          <TextField
            label="Fiber (g)"
            type="number"
            value={quickLogMacros.fiber}
            onChange={(e) =>
              setQuickLogMacros({ ...quickLogMacros, fiber: e.target.value })
            }
            fullWidth
            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="bordered"
              onClick={() => setQuickLogOpen(false)}
              disabled={createQuickLogMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="prominent"
              onClick={() => createQuickLogMutation.mutate()}
              disabled={
                createQuickLogMutation.isPending || !quickLogMacros.calories
              }
            >
              {createQuickLogMutation.isPending ? 'Adding...' : 'Add Quick Log'}
            </Button>
          </Stack>
        </Stack>
      </Dialog>

      {/* Food Item Details Modal */}
      <Dialog
        open={foodItemModalOpen}
        onClose={() => {
          setFoodItemModalOpen(false);
          setSelectedFoodItem(null);
          setFoodItemDetails(null);
          setSelectedServing(0);
        }}
        maxWidth="md"
        fullWidth
      >
        <Stack spacing={3}>
          {isLoadingDetails ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Loading details...</Typography>
            </Box>
          ) : foodItemDetails ? (
            <>
              <Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  {foodItemDetails.name}
                </Typography>
                {foodItemDetails.brand && (
                  <Typography variant="body2" color="text.secondary">
                    Brand: {foodItemDetails.brand}
                  </Typography>
                )}
                <Typography variant="caption" color="primary.main">
                  Source: {foodItemDetails.source}
                </Typography>
                {foodItemDetails.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {foodItemDetails.description}
                  </Typography>
                )}
              </Box>

              {/* Serving Selector */}
              {foodItemDetails.servings &&
                foodItemDetails.servings.length > 0 && (
                  <>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Select Serving Size
                      </Typography>
                      <TextField
                        label="Serving"
                        value={selectedServing}
                        onChange={(e) =>
                          setSelectedServing(Number(e.target.value))
                        }
                        options={foodItemDetails.servings.map(
                          (serving, idx) => ({
                            value: idx,
                            label:
                              serving.serving_description ||
                              `${serving.amount} ${serving.unit}`,
                          })
                        )}
                        fullWidth
                      />
                    </Box>

                    {/* Selected Serving Nutrition Details */}
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: 'primary.main',
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        Nutrition Facts
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        Per{' '}
                        {foodItemDetails.servings[selectedServing]
                          ?.serving_description || 'serving'}
                      </Typography>

                      <Grid container spacing={2}>
                        {/* Main Macros - Larger */}
                        {foodItemDetails.servings[selectedServing]
                          ?.calories && (
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: 'primary.light',
                                borderRadius: 1,
                                textAlign: 'center',
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Calories
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {
                                  foodItemDetails.servings[selectedServing]
                                    .calories
                                }
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {foodItemDetails.servings[selectedServing]?.protein && (
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: 'info.light',
                                borderRadius: 1,
                                textAlign: 'center',
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Protein
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {
                                  foodItemDetails.servings[selectedServing]
                                    .protein
                                }
                                g
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {foodItemDetails.servings[selectedServing]
                          ?.carbohydrate && (
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: 'warning.light',
                                borderRadius: 1,
                                textAlign: 'center',
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Carbs
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {
                                  foodItemDetails.servings[selectedServing]
                                    .carbohydrate
                                }
                                g
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {foodItemDetails.servings[selectedServing]?.fat && (
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: 'error.light',
                                borderRadius: 1,
                                textAlign: 'center',
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Fat
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {foodItemDetails.servings[selectedServing].fat}g
                              </Typography>
                            </Box>
                          </Grid>
                        )}

                        {/* Additional Nutrition Info - Smaller */}
                        {foodItemDetails.servings[selectedServing]?.fiber && (
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <Box sx={{ p: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Fiber
                              </Typography>
                              <Typography variant="body1" fontWeight="600">
                                {
                                  foodItemDetails.servings[selectedServing]
                                    .fiber
                                }
                                g
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {foodItemDetails.servings[selectedServing]?.sugar && (
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <Box sx={{ p: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Sugar
                              </Typography>
                              <Typography variant="body1" fontWeight="600">
                                {
                                  foodItemDetails.servings[selectedServing]
                                    .sugar
                                }
                                g
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {foodItemDetails.servings[selectedServing]
                          ?.saturated_fat && (
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <Box sx={{ p: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Saturated Fat
                              </Typography>
                              <Typography variant="body1" fontWeight="600">
                                {
                                  foodItemDetails.servings[selectedServing]
                                    .saturated_fat
                                }
                                g
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {foodItemDetails.servings[selectedServing]
                          ?.cholesterol && (
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <Box sx={{ p: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Cholesterol
                              </Typography>
                              <Typography variant="body1" fontWeight="600">
                                {
                                  foodItemDetails.servings[selectedServing]
                                    .cholesterol
                                }
                                mg
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {foodItemDetails.servings[selectedServing]?.sodium && (
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <Box sx={{ p: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Sodium
                              </Typography>
                              <Typography variant="body1" fontWeight="600">
                                {
                                  foodItemDetails.servings[selectedServing]
                                    .sodium
                                }
                                mg
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {foodItemDetails.servings[selectedServing]
                          ?.potassium && (
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <Box sx={{ p: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Potassium
                              </Typography>
                              <Typography variant="body1" fontWeight="600">
                                {
                                  foodItemDetails.servings[selectedServing]
                                    .potassium
                                }
                                mg
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </>
                )}

              {/* Add to Meal Section */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: existingEntry ? '#fff3e0' : 'primary.light',
                  borderRadius: 1,
                }}
              >
                {existingEntry ? (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, color: 'warning.main' }}
                    >
                      ⚠️ Already added to {existingEntry.food_item.name} (
                      {existingEntry.quantity} {existingEntry.unit})
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="bordered"
                        color="error"
                        onClick={() => {
                          deleteEntryMutation.mutate(existingEntry.id);
                          setFoodItemModalOpen(false);
                        }}
                        disabled={deleteEntryMutation.isPending}
                        fullWidth
                      >
                        {deleteEntryMutation.isPending
                          ? 'Deleting...'
                          : 'Delete'}
                      </Button>
                      <Button
                        variant="bordered"
                        color="primary"
                        onClick={() => {
                          setEditingEntryId(existingEntry.id);
                          setEditQuantity(existingEntry.quantity);
                          setEditUnit(existingEntry.unit);
                        }}
                        fullWidth
                      >
                        Edit
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Button
                    variant="prominent"
                    color="primary"
                    onClick={() => {
                      addSelectedFoodEntryMutation.mutate();
                      setFoodItemModalOpen(false);
                    }}
                    disabled={addSelectedFoodEntryMutation.isPending}
                    fullWidth
                    size="large"
                  >
                    {addSelectedFoodEntryMutation.isPending
                      ? 'Adding...'
                      : `Add to ${mealType}`}
                  </Button>
                )}
              </Box>

              {foodItemDetails.food_url && (
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="borderless"
                    size="small"
                    href={foodItemDetails.food_url}
                    rel="noopener noreferrer"
                  >
                    View on FatSecret ↗
                  </Button>
                </Box>
              )}
            </>
          ) : null}
        </Stack>
      </Dialog>
    </Container>
  );
};

export default NutritionTestPage;
